import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';

import type { IngresoCuenta } from '#components/mobile/inicio/dosNominas';
import { aqlQuery } from '#queries/aqlQuery';

const CADUCIDAD_MS = 30_000;

export type DatosCuentaComun = {
  /** Salidas por mes ('yyyy-MM'), en positivo, sin traspasos. */
  salidas: Record<string, IntegerAmount>;
};

/**
 * Salidas mensuales de la cuenta común sin traspasos, para el gasto habitual.
 */
export function useDatosCuentaComun(
  cuentaId: string | undefined,
  mes: string,
  meses: number,
) {
  const desde = monthUtils.subMonths(mes, meses);
  return useQuery({
    queryKey: ['inicio', 'cuenta-comun', cuentaId, desde, mes],
    enabled: Boolean(cuentaId),
    staleTime: CADUCIDAD_MS,
    queryFn: async (): Promise<DatosCuentaComun> => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            account: cuentaId,
            amount: { $lt: 0 },
            'payee.transfer_acct': null,
            $and: [
              { date: { $transform: '$month', $gte: desde } },
              { date: { $transform: '$month', $lte: mes } },
            ],
          })
          .groupBy({ $month: '$date' })
          .select([
            { date: { $month: '$date' } },
            { amount: { $sum: '$amount' } },
          ]),
      );
      const salidas: Record<string, IntegerAmount> = {};
      for (const f of filas as { date: string; amount: number }[]) {
        salidas[f.date] = Math.abs(f.amount);
      }
      return { salidas };
    },
  });
}

/**
 * Ingresos de la cuenta común (sin traspasos) de los 6 meses anteriores y
 * este, para encontrar las nóminas que entran en ella.
 */
export function useIngresosCuentaComun(
  cuentaId: string | undefined,
  mes: string,
) {
  const desde = monthUtils.subMonths(mes, 6);
  return useQuery({
    queryKey: ['inicio', 'cuenta-comun-ingresos', cuentaId, desde, mes],
    enabled: Boolean(cuentaId),
    staleTime: CADUCIDAD_MS,
    queryFn: async (): Promise<IngresoCuenta[]> => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            account: cuentaId,
            amount: { $gt: 0 },
            'payee.transfer_acct': null,
            $and: [
              { date: { $transform: '$month', $gte: desde } },
              { date: { $transform: '$month', $lte: mes } },
            ],
          })
          .options({ splits: 'inline' })
          .select(['payee', 'date', 'amount']),
      );
      return filas as IngresoCuenta[];
    },
  });
}
